import { useState, useEffect, useRef } from 'react';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { logger } from '@/features/help/utils/logger';
import { fileService } from '../services/file.service';
import { FieldValue } from '../types/field-value';
import toast from 'react-hot-toast';

interface UseFormContentProps {
  mappingShortId?: string;
}

interface UseFormContentReturn {
  // Terms consent
  isTermsInvalid: boolean;
  handleTermsConsentChange: (checked: boolean) => void;

  // Navigation handlers
  handleNextPageWithValidation: (
    isLastPage: boolean,
    formRef: React.RefObject<HTMLDivElement>
  ) => Promise<void>;
  handlePreviousPageNavigation: () => void;

  // Form state
  formRef: React.RefObject<HTMLDivElement>;
}

/**
 * Custom hook to handle FormContent logic including live signer sync,
 * file caching, terms consent, and navigation with validation
 */
export const useFormContent = ({ mappingShortId }: UseFormContentProps): UseFormContentReturn => {
  const { setTermsConsent, termsConsent, handleFieldValueChange } = useApplicationStore();

  const { handleNextPage, handlePreviousPage, getCurrentPage } = useNavigationStore();

  const [isTermsInvalid, setIsTermsInvalid] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const currentPage = getCurrentPage();

  // Load cached files for file fields on mount
  useEffect(() => {
    if (!currentPage?.sections) return;

    currentPage.sections.forEach((section: any) => {
      section.fields.forEach((field: any) => {
        if (field.type === 'file') {
          // Check if we have files in the cache AND the field doesn't already have a value
          if (
            fileService.hasFiles(field.id) &&
            (!field.value || (Array.isArray(field.value) && field.value.length === 0))
          ) {
            const cachedFiles = fileService.getFiles(field.id);
            logger.debug('FormContent Hook', 'Loaded files from cache for field', {
              fieldId: field.id,
              fileCount: cachedFiles.length,
            });

            // Update the field value with cached files
            if (cachedFiles.length > 0) {
              handleFieldValueChange(field.id, cachedFiles as FieldValue);
            }
          }
        }
      });
    });
  }, [currentPage, handleFieldValueChange]);

  // Handle terms consent change
  const handleTermsConsentChange = (checked: boolean) => {
    setTermsConsent(checked);

    if (checked) {
      setIsTermsInvalid(false);
    }

    logger.debug('FormContent Hook', 'Terms consent updated', {
      termsConsent: checked,
      mappingShortId,
    });
  };

  // Handle next page with validation for terms consent
  const handleNextPageWithValidation = async (
    isLastPage: boolean,
    formRefElement: React.RefObject<HTMLDivElement>
  ) => {
    logger.debug('FormContent Hook', 'handleNextPageWithValidation called', {
      isLastPage,
      hasFormElement: !!formRefElement.current,
      termsConsent,
    });

    if (isLastPage && !termsConsent) {
      setIsTermsInvalid(true);
      toast.error('You must agree to the terms to continue');
      logger.info('FormContent Hook', 'Terms consent required but not given', {
        isLastPage,
        termsConsent,
      });
      return;
    }

    await handleNextPage(formRefElement.current);
  };

  const handlePreviousPageNavigation = () => {
    logger.debug('FormContent Hook', 'handlePreviousPageNavigation called', {
      mappingShortId,
    });

    handlePreviousPage();
  };

  return {
    // Terms consent
    isTermsInvalid,
    handleTermsConsentChange,

    // Navigation handlers
    handleNextPageWithValidation,
    handlePreviousPageNavigation,

    // Form state
    formRef,
  };
};
